import { $api } from '~/utils/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

export type MemberType = 'board' | 'monitor'

interface MonitorFilters {
  page?: number
  limit?: number
  search?: string
  isActive?: boolean
  memberType?: MemberType
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface MonitorInput {
  name: string
  role: string
  job?: string | null
  diploma?: string | null
  bio?: string | null
  image_url?: string | null
  display_order?: number
  is_active?: boolean
  member_type?: MemberType
}

export const useMonitors = () => {
  const supabase = useSupabaseClient()

  /**
   * Fetch monitors for public display (no auth required)
   * Only returns active monitors ordered by display_order
   * @param limit - Maximum number of monitors to fetch
   * @param memberType - Filter by member type ('board' or 'monitor')
   */
  const getPublicMonitors = async (limit = 100, memberType?: MemberType) => {
    try {
      const params = new URLSearchParams({ limit: String(limit) })
      if (memberType) {
        params.append('member_type', memberType)
      }

      return await $api(`/api/monitors/public?${params.toString()}`)
    }
    catch (error) {
      logApiError('/api/monitors/public', error, { limit })
      throw error
    }
  }

  const getMonitors = async (filters: MonitorFilters = {}) => {
    try {
      const queryString = buildQueryString(filters)

      return await $api(`/api/monitors${queryString ? `?${queryString}` : ''}`)
    }
    catch (error) {
      logApiError('/api/monitors', error, { filters })
      throw error
    }
  }

  const getMonitor = async (id: string) => {
    try {
      return await $api(`/api/monitors/${id}`)
    }
    catch (error) {
      logApiError(`/api/monitors/${id}`, error)
      throw error
    }
  }

  const createMonitor = async (data: MonitorInput) => {
    try {
      return await $api('/api/monitors', {
        method: 'POST',
        body: data,
      })
    }
    catch (error) {
      logApiError('/api/monitors', error, { data })
      throw error
    }
  }

  const updateMonitor = async (id: string, data: Partial<MonitorInput>) => {
    try {
      return await $api<unknown>(`/api/monitors/${id}`, {
        method: 'PATCH',
        body: data,
      })
    }
    catch (error) {
      logApiError(`/api/monitors/${id}`, error, { data })
      throw error
    }
  }

  const deleteMonitor = async (id: string) => {
    try {
      return await $api<void>(`/api/monitors/${id}`, {
        method: 'DELETE',
      })
    }
    catch (error) {
      logApiError(`/api/monitors/${id}`, error)
      throw error
    }
  }

  const updateMonitorOrder = async (
    items: Array<{ id: string, display_order: number }>,
  ) => {
    try {
      // Update order for multiple monitors
      const promises = items.map(item =>
        updateMonitor(item.id, { display_order: item.display_order }),
      )

      return await Promise.all(promises)
    }
    catch (error) {
      logApiError('/api/monitors (bulk update)', error, { items })
      throw error
    }
  }

  const uploadMonitorImage = async (
    file: File,
    monitorId: string,
  ): Promise<string | null> => {
    try {
      // Validate file type
      const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/webp',
      ]

      if (!allowedTypes.includes(file.type))
        throw new Error('Alleen JPG, PNG en WEBP afbeeldingen zijn toegestaan')

      // Validate file size (5MB max)
      const maxSize = 5 * 1024 * 1024 // 5MB in bytes
      if (file.size > maxSize)
        throw new Error('Afbeelding mag maximaal 5MB zijn')

      // Generate unique file path
      const fileExt = file.name.split('.').pop()
      const fileName = `${monitorId}-${Date.now()}.${fileExt}`
      const filePath = `monitors/${fileName}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('monitor-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        })

      if (uploadError) {
        logApiError('supabase/storage/upload', uploadError, { filePath })
        throw new Error(`Upload mislukt: ${uploadError.message}`)
      }

      // Get public URL
      const { data } = supabase.storage
        .from('monitor-images')
        .getPublicUrl(filePath)

      return data.publicUrl
    }
    catch (error) {
      logApiError('uploadMonitorImage', error, {
        fileName: file.name,
        monitorId,
      })
      throw error
    }
  }

  const deleteMonitorImage = async (imageUrl: string): Promise<void> => {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/monitor-images/')
      if (urlParts.length !== 2)
        throw new Error('Ongeldige afbeelding URL')

      const filePath = urlParts[1]!

      const { error } = await supabase.storage
        .from('monitor-images')
        .remove([filePath])

      if (error) {
        logApiError('supabase/storage/delete', error, { filePath })
        throw new Error(`Verwijderen mislukt: ${error.message}`)
      }
    }
    catch (error) {
      logApiError('deleteMonitorImage', error, { imageUrl })
      throw error
    }
  }

  return {
    getPublicMonitors,
    getMonitors,
    getMonitor,
    createMonitor,
    updateMonitor,
    deleteMonitor,
    updateMonitorOrder,
    uploadMonitorImage,
    deleteMonitorImage,
  }
}
